import { useMemo, useState } from 'react';
import { useDebouncedValue } from '@/hooks/use-debounced-value';
import { ComboboxField, type ComboboxOption } from '@/components/combobox-field';
import { useBlSearch, type BlSearchHit } from '../api/bl-search';
import type { RaiseFormValues } from '../lib/raise-form-schema';

// Searchable BL picker for the raise dialog (launchpad drawer parity); mirrors
// the order picker so BL-scope raises pick an existing BL instead of typing an id.
export function BlComboboxField({
  currentId,
  disabled,
}: Readonly<{
  currentId: string;
  disabled: boolean;
}>) {
  const [search, setSearch] = useState('');
  const { data, isLoading } = useBlSearch(useDebouncedValue(search, 300));
  const options = useMemo(() => buildOptions(data ?? [], currentId), [data, currentId]);
  return (
    <ComboboxField<RaiseFormValues>
      name="blOrderId"
      label="BL Order"
      className="sm:col-span-2"
      options={options}
      placeholder="Search BL…"
      searchPlaceholder="BL name or number…"
      emptyMessage="No matching BLs."
      searchable
      searchValue={search}
      onSearchChange={setSearch}
      loading={isLoading}
      disabled={disabled}
    />
  );
}

function buildOptions(hits: readonly BlSearchHit[], currentId: string): ComboboxOption[] {
  const options = hits.map(toOption);
  // Keep the selected BL visible even if the search no longer returns it.
  if (currentId !== '' && !options.some((o) => o.value === currentId)) {
    options.unshift({ value: currentId, label: currentId });
  }
  return options;
}

function toOption(hit: BlSearchHit): ComboboxOption {
  return {
    value: hit.id,
    label: hit.display,
    ...(hit.secondary === null ? {} : { description: hit.secondary }),
  };
}
